import { CardSummary } from "projects/frontend-client/export";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import {
  isFormValueExpired,
  isPaymentMethodExpired,
} from "checkout-experience/utils/payment-helpers";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";

import { useEnabledSavedPaymentMethods } from "./use-enabled-saved-payment-methods";

export const useIsExpired = () => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const isSavedPaymentMethod =
    paymentOption?.formId !== paymentOption?.paymentType;
  const enabledSavedMOPs = useEnabledSavedPaymentMethods();
  const { watch } = useFormContext();

  return useMemo(() => {
    if (!isSavedPaymentMethod) {
      return false;
    }

    /**
     * If selected saved MOP is in edit mode then we check the expiration from form state
     * If the edit mode is not initiated, then there will be no form fields for selected saved MOP
     * So we resolve to checkign expiration value from checkout session
     */
    const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;
    if (
      formFields?.expiration ||
      (formFields?.expirationMonth && formFields?.expirationYear)
    ) {
      return isFormValueExpired(
        formFields?.expiration,
        formFields?.expirationMonth,
        formFields?.expirationYear
      );
    } else {
      const paymentMethod = enabledSavedMOPs?.find(
        (p) => p.paymentMethodId === paymentOption?.formId
      );

      const card =
        paymentMethod?.paymentMethod.case === "card"
          ? (paymentMethod?.paymentMethod.value as CardSummary)
          : undefined;
      if (!card) {
        return false;
      }

      return isPaymentMethodExpired({
        expirationMonth: card.expirationMonth,
        expirationYear: card.expirationYear,
      });
    }
  }, [isSavedPaymentMethod, paymentOption, enabledSavedMOPs]);
};
